/**
 * The survey domain (BE-141, 2026-10-12) — one door for every survey definition and ONE
 * response per (user, survey) for the profile surveys. Bodies and responses pass through the
 * case interceptor: `answers` is keyed by SECTION ID, and the ids are the config's own
 * snake_case words (`work_mode`), which the camel → snake transform leaves as they are — the
 * same way the capsule survey has always sent them.
 */

import { apiClient } from '@/api/api'
import type { SurveyStep } from '@/features/style-space/api/types'

export type SurveyKind = 'sizes' | 'capsule' | 'lifestyle' | 'style'

export const LIFESTYLE_SURVEY_KEY = 'default-lifestyle-survey'

/** `GET /surveys/{key}/config` — the stored definition as is (the capsule-survey step format). */
export interface SurveyDefinition {
  key: string
  kind: SurveyKind
  version: number
  title: string
  config: {
    id: string
    title: string
    steps: SurveyStep[]
  }
}

/** One answer of `PUT /surveys/{key}/response` — the capsule-survey answer shape or a bare string. */
export type SurveyResponseAnswer =
  | string
  | {
      value: string | string[] | null
      isCustom?: boolean
      multiAnswer?: boolean
      customText?: string | null
    }

export interface SurveyResponseBody {
  answers: Record<string, SurveyResponseAnswer>
  /** the closing step; at most 2000 characters */
  freeText?: string | null
}

export interface SurveyResponse {
  surveyKey: string
  answers: Record<string, unknown>
  freeText: string | null
  updatedAt: string | null
}

export const getSurveyDefinition = (key: string) =>
  apiClient.get<SurveyDefinition>(`/surveys/${encodeURIComponent(key)}/config`)

/** 404 before the first PUT. */
export const getSurveyResponse = (key: string) =>
  apiClient.get<SurveyResponse>(`/surveys/${encodeURIComponent(key)}/response`)

/** 409 for `sizes` / `capsule` (their own submit routes derive sizes / resolve tags). */
export const putSurveyResponse = (key: string, body: SurveyResponseBody) =>
  apiClient.put<SurveyResponse>(`/surveys/${encodeURIComponent(key)}/response`, body)

export const deleteSurveyResponse = (key: string) =>
  apiClient.delete<{ surveyKey: string; deleted: number }>(`/surveys/${encodeURIComponent(key)}/response`)

/* ——— Lifestyle (BE-141) ——— */

export interface UserLifestyleAnswer {
  questionId: string
  answerKey: string
  /** the server's label of the chosen option */
  label: string
}

/** `GET /user/lifestyle` — the seven answers with their labels and the note. */
export interface UserLifestyle {
  note: string | null
  answers: UserLifestyleAnswer[]
  updatedAt: string | null
  completed: boolean
}

export const getUserLifestyle = () => apiClient.get<UserLifestyle>('/user/lifestyle')

/** Clears both the answers and the note; `taste.styles[]` never changes from it. */
export const deleteUserLifestyle = () => apiClient.delete<{ deleted: number }>('/user/lifestyle')
